import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import Pagination from '@/components/ui/pagination/Pagination'
import TextInput from '@/components/ui/inputs/TextInput'

import { ScheduleAvailability } from '@/core/models/schedule.model'

import StaffAvailability, { StaffAvailabilityProps } from './StaffAvailability'

const emptyArray: unknown = []

const MemoizedStaffAvailability = React.memo(StaffAvailability)

export type SelectStaffAvailabilityStepProps = {
  availabilitiesByStaff?: Record<string, ScheduleAvailability>
  calendsoAppointmentTypesByStaff: Record<string, StaffAvailabilityProps['calendsoAppointmentTypes']>
  calendsoAvailabilitiesByStaff?: Record<string, ScheduleAvailability>
  defaultStartDate?: Date
  disableSearchStaffs?: boolean
  disableAddStaff?: boolean
  selectedStaff?: string
  staffs: Array<StaffAvailabilityProps['staff']>
  isDoctor?: boolean
  onOpenAddDoctorDialog?: () => void
  onSearchStaffAvailability?: StaffAvailabilityProps['onCalendarViewChange']
  onSelectStaff: (staffId: string) => void
  onSelectStartDate?: (startDate: Date | null, duration?: number, appointmentTypeId?: number) => void
  onTextSearch?: (text: string) => void
  rowsOptions?: number[]
  onPageChange?: (newPage: number, rowsPerPage: number) => void
  totalStaffs: number
  showOnlyAvailability?: boolean
}

export function SelectStaffAvailabilityStep(props: SelectStaffAvailabilityStepProps) {
  const {
    availabilitiesByStaff,
    calendsoAppointmentTypesByStaff,
    calendsoAvailabilitiesByStaff,
    defaultStartDate,
    disableAddStaff = false,
    disableSearchStaffs = false,
    isDoctor,
    onOpenAddDoctorDialog,
    onSearchStaffAvailability,
    onSelectStaff,
    onSelectStartDate,
    onTextSearch,
    onPageChange,
    rowsOptions,
    selectedStaff: selectedStaffProp,
    staffs,
    totalStaffs,
    showOnlyAvailability,
  } = props
  const { t } = useTranslation()

  const [page, setPage] = useState<number>(1)
  const [, setRowsPerPage] = useState<number>(rowsOptions?.[0] || 5)
  const [selectedStaffId, setSelectedStaffId] = useState<string | undefined>(selectedStaffProp)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onTextSearch?.(event.target.value)
  }

  const handleChangeStartDate = useCallback(
    (startDate: Date | null, duration?: number, appointmentTypeId?: number) => {
      onSelectStartDate?.(startDate, duration, appointmentTypeId)
    },
    [onSelectStartDate]
  )

  const handleSelectStaff = useCallback(
    (staffId: string, startDate: Date | null) => {
      setSelectedStaffId(staffId)
      onSelectStartDate?.(startDate)
      onSelectStaff?.(staffId)
    },
    [onSelectStaff, onSelectStartDate]
  )

  const handleChangePage = (newPage: number, rowsPerPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage, rowsPerPage)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    if (Math.ceil(totalStaffs / newRowsPerPage) >= page) onPageChange?.(page, newRowsPerPage)
  }

  return (
    <Stack spacing={2}>
      {(!disableAddStaff || !disableSearchStaffs) && (
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} alignItems="center" spacing={2} justifyContent="space-between">
          {!disableSearchStaffs && (
            <Box flex={1}>
              <TextInput
                fullWidth
                id={`search-${isDoctor ? 'pds' : 'doctor'}`}
                onChange={handleChange}
                placeholder={t(`page.createConsultation.step.${isDoctor ? 'pds' : 'doctor'}AvailabilitySelection.searchInput.placeholder`)}
              />
            </Box>
          )}
          {!disableAddStaff && !isDoctor && (
            <Button onClick={onOpenAddDoctorDialog} variant="contained" id="invite-doctor">
              {t('page.createConsultation.step.doctorAvailabilitySelection.createDoctorButton.label')}
            </Button>
          )}
        </Stack>
      )}
      <Stack spacing={2} mb="32px" id="staff-list">
        {staffs.map((staff, index) => (
          <Box key={staff.id} id={`${isDoctor ? 'pds' : 'doctor'}_${index}`}>
            <MemoizedStaffAvailability
              calendsoAppointmentTypes={calendsoAppointmentTypesByStaff[staff.id] || (emptyArray as StaffAvailabilityProps['calendsoAppointmentTypes'])}
              busySlots={
                availabilitiesByStaff?.[staff.id]?.busySlots ??
                calendsoAvailabilitiesByStaff?.[staff.id]?.busySlots ??
                (emptyArray as StaffAvailabilityProps['busySlots'])
              }
              defaultStartDate={defaultStartDate}
              isSelected={staff.id === selectedStaffId}
              onCalendarViewChange={onSearchStaffAvailability}
              onChangeStartDate={handleChangeStartDate}
              onSelectStaff={handleSelectStaff}
              staff={staff}
              workingHours={
                availabilitiesByStaff?.[staff.id]?.workingHours ??
                calendsoAvailabilitiesByStaff?.[staff.id]?.workingHours ??
                (emptyArray as StaffAvailabilityProps['workingHours'])
              }
              timeZone={availabilitiesByStaff?.[staff.id]?.timeZone ?? calendsoAvailabilitiesByStaff?.[staff.id]?.timeZone}
              showOnlyAvailability={showOnlyAvailability}
            />
          </Box>
        ))}
        {!selectedStaffProp && (
          <Stack direction="row" justifyContent="end">
            <Pagination
              color="primary"
              itemsPerPageOptions={rowsOptions}
              shape="rounded"
              page={page}
              onPageChange={handleChangePage}
              onItemsPerPageChange={handleRowsPerPageChange}
              totalItems={totalStaffs}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default SelectStaffAvailabilityStep
